import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import {
  Bell,
  CalendarBlank,
  ChartBar,
  Clock,
  Gear,
  Key,
  Link as LinkIcon,
  Lock,
  Megaphone,
  ShieldCheck,
  Sparkle,
  Storefront,
  User,
  UserCircle,
} from '@phosphor-icons/react';

export type StaffSettingsPath =
  | '/subdomain/admin/settings/profile'
  | '/subdomain/admin/settings/security'
  | '/subdomain/admin/settings/notifications'
  | '/subdomain/admin/settings/availability'
  | '/subdomain/admin/settings/connections'
  | '/subdomain/admin/settings/workspace/agency'
  | '/subdomain/admin/settings/workspace/security-policy'
  | '/subdomain/admin/settings/workspace/staff-security'
  | '/subdomain/admin/settings/workspace/roles'
  | '/subdomain/admin/settings/workspace/flags'
  | '/subdomain/admin/settings/workspace/maintenance'
  | '/subdomain/admin/settings/workspace/scheduling'
  | '/subdomain/admin/settings/workspace/team-notifications'
  | '/subdomain/admin/settings/workspace/integrations'
  | '/subdomain/admin/settings/workspace/limits';

export interface StaffSettingsNavItem {
  label: string;
  icon: PhosphorIcon;
  to: StaffSettingsPath;
}

export interface StaffSettingsNavGroup {
  heading: string;
  /** Workspace pages are admin-only: the group is hidden for everyone else (Figma A16.16). */
  adminOnly: boolean;
  items: StaffSettingsNavItem[];
}

const P = '/subdomain/admin/settings';

/** Figma "Admin/Settings nav": MY SETTINGS for every staff role, WORKSPACE · ADMIN for admins. */
export const STAFF_SETTINGS_NAV: StaffSettingsNavGroup[] = [
  {
    heading: 'My settings',
    adminOnly: false,
    items: [
      { label: 'Profile', icon: User, to: `${P}/profile` },
      { label: 'Security', icon: ShieldCheck, to: `${P}/security` },
      { label: 'Notifications', icon: Bell, to: `${P}/notifications` },
      { label: 'Availability', icon: Clock, to: `${P}/availability` },
      { label: 'Connected accounts', icon: LinkIcon, to: `${P}/connections` },
    ],
  },
  {
    heading: 'Workspace · Admin',
    adminOnly: true,
    items: [
      { label: 'Agency profile', icon: Storefront, to: `${P}/workspace/agency` },
      { label: 'Security policy', icon: Lock, to: `${P}/workspace/security-policy` },
      { label: 'Staff 2FA & sessions', icon: Key, to: `${P}/workspace/staff-security` },
      { label: 'Roles & permissions', icon: UserCircle, to: `${P}/workspace/roles` },
      { label: 'Feature flags', icon: Sparkle, to: `${P}/workspace/flags` },
      { label: 'Maintenance mode', icon: Gear, to: `${P}/workspace/maintenance` },
      { label: 'Scheduling rules', icon: CalendarBlank, to: `${P}/workspace/scheduling` },
      { label: 'Team notifications', icon: Megaphone, to: `${P}/workspace/team-notifications` },
      { label: 'Integrations', icon: LinkIcon, to: `${P}/workspace/integrations` },
      { label: 'Limits', icon: ChartBar, to: `${P}/workspace/limits` },
    ],
  },
];

export const settingsNavFor = (role: string | null | undefined): StaffSettingsNavGroup[] =>
  STAFF_SETTINGS_NAV.filter((group) => !group.adminOnly || role === 'admin');
